<template>
  <page-content-sidebar-list-title
    v-if="itemType === 'group'"
    :title="title"
  />

  <page-content-sidebar-list-simple
    v-else-if="itemType === 'simple'"
    :class="{ 'active-item': isActive }"
    :title="title"
    :badge="badge"
    :right-icon="item?.itemType === 'simple' ? item.rightIcon : undefined"
    :highlight-exact-active="isActive"
    @click="$emit('click', item!)"
  >
    <template v-slot:icon>
      <slot name="icon" />
    </template>
    <template v-slot:menu-item-counter>
      <slot name="menu-item-counter" />
    </template>
  </page-content-sidebar-list-simple>

  <page-content-sidebar-list-group-collapsible
    v-else-if="itemType === 'groupCollapsible'"
    :opened="isOpenedGroupCollapsible"
    :title="title"
    :toggle-icon-font-size="toggleIconFontSize"
    :is-collapsing-disabled="item && isPageContentSidebarGroupCollapsible(item) ? item.isCollapsingDisabled : undefined"
    :hide-collapsed-hr="item && isPageContentSidebarGroupCollapsible(item) ? item.hideCollapsedHr : undefined"
    @update:opened="isOpenedGroupCollapsible = $event"
  >
    <template v-slot:title>
      <slot name="title" />
    </template>
    <slot
      name="items"
      :items="links"
      @click="$emit('click', $event)"
    >
      <page-content-sidebar-list
        :items="links || []"
        @click="$emit('click', $event)"
      />
    </slot>
  </page-content-sidebar-list-group-collapsible>

  <page-content-sidebar-list-link
    v-else
    :title="title"
    :badge="badge"
    :highlight-exact-active="highlightExactActive"
    :in-group="inGroup"
    :override-active="overrideActive"
    :right-icon="item?.itemType === 'link' ? item.rightIcon : undefined"
    @lock-sidebar="$emit('lock-sidebar', $event)"
  >
    <template v-slot:icon>
      <slot name="icon" />
    </template>
    <template v-slot:after-link-content>
      <slot name="after-link-content" />
    </template>
  </page-content-sidebar-list-link>
</template>

<script setup lang="ts">
import {
  isPageContentSidebarGroupCollapsible,
  type PageContentSidebarItem,
  type PageContentSidebarItemTypes,
  type PageContentSidebarLink,
  type PageContentSidebarSimpleItem,
} from '@/components/layout/pageContent/sidebar/types'
import PageContentSidebarListTitle from '@/components/layout/pageContent/sidebar/sidebarItem/PageContentSidebarListTitle.vue'
import PageContentSidebarListLink from '@/components/layout/pageContent/sidebar/sidebarItem/PageContentSidebarListLink.vue'
import PageContentSidebarListGroupCollapsible from '@/components/layout/pageContent/sidebar/sidebarItem/PageContentSidebarListGroupCollapsible.vue'
import PageContentSidebarList from '@/components/layout/pageContent/sidebar/PageContentSidebarList.vue'
import PageContentSidebarListSimple from '@/components/layout/pageContent/sidebar/sidebarItem/PageContentSidebarListSimple.vue'
import { ref, watch } from '@/composition/vue/compositionApi'

const props = defineProps<{
  itemType?: PageContentSidebarItemTypes
  title: string
  links?: Array<PageContentSidebarLink | PageContentSidebarSimpleItem>
  badge?: PageContentSidebarLink['badge']
  highlightExactActive?: boolean
  inGroup?: boolean
  item?: PageContentSidebarItem
  isActive?: boolean
  toggleIconFontSize?: number
  overrideActive?: boolean
}>()

const emit = defineEmits<{
  'lock-sidebar': [boolean]
  click: [PageContentSidebarItem]
  collapse: []
  expand: []
}>()

const isOpenedGroupCollapsible = ref(
  props.item && isPageContentSidebarGroupCollapsible(props.item) ? !props.item.collapsed : true,
)

const changeIsOpenedGroupCollapsible = (val: boolean) => {
  isOpenedGroupCollapsible.value = val
}

watch(
  isOpenedGroupCollapsible,
  (opened) => {
    if (opened) {
      emit('expand')
    } else {
      emit('collapse')
    }
  },
  { immediate: true },
)

defineExpose({
  changeIsOpenedGroupCollapsible,
})
</script>
